import { useMemo } from "react"

import { useAdminViewContext } from "#contexts/AdminViewContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import {
  FRONT_DESK_ROOM,
  HelpRequestRoom,
} from "#customTypes/composedTypes/helpRequestTypes"
import useHasPermission from "#hooks/newHooks/useHasPermission"
import { getNow } from "#utils/dateHelpers"

/**
 * Derives the effective user's current room from today's sessions.
 * Checks both teacherIds and assistantIds.
 * Picks the session the user is currently in (now between start/end),
 * falling back to the next upcoming session, then the most recent past session.
 * For FRONT_DESK+ users not in a class, defaults to FRONT_DESK.
 */
export const useCurrentUserRoom = (): HelpRequestRoom | undefined => {
  const { effectiveUserId } = useUserContext()
  const { allSessions } = useAdminViewContext()
  const isFrontDeskPlus = useHasPermission(
    RESOURCES.FRONT_DESK_VIEW,
    USER_ACTIONS.READ
  )

  return useMemo(() => {
    if (!effectiveUserId || allSessions.length === 0) {
      return isFrontDeskPlus ? FRONT_DESK_ROOM : undefined
    }

    const now = getNow()

    const mySessions = allSessions.filter((s) => {
      const { teacherIds, assistantIds } = s.session
      return (
        teacherIds?.includes(effectiveUserId) ||
        assistantIds?.includes(effectiveUserId)
      )
    })

    if (mySessions.length === 0) {
      return isFrontDeskPlus ? FRONT_DESK_ROOM : undefined
    }

    const current = mySessions.find(
      (s) => now.isAfter(s.startTime) && now.isBefore(s.endTime)
    )
    if (current) return current.room

    const upcoming = mySessions
      .filter((s) => now.isBefore(s.startTime))
      .sort((a, b) => a.startTime.diff(b.startTime))
    if (upcoming.length > 0) return upcoming[0].room

    const past = mySessions
      .filter((s) => now.isAfter(s.endTime))
      .sort((a, b) => b.endTime.diff(a.endTime))
    if (past.length > 0) return past[0].room

    return isFrontDeskPlus ? FRONT_DESK_ROOM : undefined
  }, [effectiveUserId, allSessions, isFrontDeskPlus])
}
